<template>
    <div class="min-h-screen bg-background flex items-center justify-center p-4">
        <div class="card-primary w-full max-w-2xl space-y-6">
            <h2 class="text-2xl font-bold text-center text-primary border-b border-gold pb-2">
                Mon compte
            </h2>

            <section>
                <h3 class="text-lg font-semibold text-gold mb-2">Mes informations</h3>
                <div class="mb-3 rounded-xl border border-gold/30 bg-backgroundLight p-3">
                    <div class="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                        <div>
                            <p class="text-xs uppercase tracking-wide text-secondary mb-1">Votre solde classement</p>
                            <router-link :to="{ name: 'UserRanking' }">
                                <span
                                    class="inline-flex items-center rounded-md bg-gold px-2 py-1 text-xs font-medium text-primary inset-ring inset-ring-gray-500/10">
                                    {{ amount }}€ ({{ amount }} pts)
                                </span>
                            </router-link>
                            <p class="text-xs text-secondary mt-2">
                                Montez plus vite dans le classement quand vous le souhaitez.
                            </p>
                        </div>

                        <router-link :to="{ name: 'BuyRanking' }"
                            class="inline-flex items-center justify-center rounded-lg border border-gold/40 px-4 py-2 text-sm font-semibold text-gold hover:bg-gold hover:text-primary transition self-start md:self-center">
                            Ajouter des points
                        </router-link>
                    </div>
                </div>
                <form @submit.prevent="updateProfile"
                    class="bg-backgroundLight p-4 rounded-lg space-y-3 border border-gold/40">
                    <div>
                        <label class="block text-sm text-secondary mb-1">Nom d’utilisateur</label>
                        <input v-model="form.username" type="text" class="input-primary" placeholder="Votre pseudo"
                            required />
                        <p v-if="user?.hasChangedUsername" class="text-xs text-blue-400 mt-1">
                            Vous avez déjà modifié votre nom d’utilisateur auparavant.
                        </p>
                    </div>

                    <div>
                        <label class="block text-sm text-secondary mb-1">Email</label>
                        <input v-model="form.email" type="email" class="input-primary"
                            placeholder="Votre adresse e-mail" required />
                    </div>

                    <div>
                        <label class="block text-sm text-secondary mb-1">Date de naissance</label>
                        <input v-model="form.birthdate" type="date" class="input-primary" />
                    </div>

                    <div>
                        <label class="block text-sm text-secondary mb-1">Bio / présentation</label>
                        <textarea
                            v-model="form.bio"
                            rows="4"
                            maxlength="500"
                            class="input-primary resize-none"
                            placeholder="Présentez-vous en quelques lignes (ville, objectif, vibe...)">
                        </textarea>
                        <p class="mt-1 text-[11px] text-secondary">{{ (form.bio || '').length }}/500</p>
                    </div>

                    <div>
                        <label class="block text-sm text-secondary mb-1">Genre</label>
                        <select v-model="form.gender" class="input-primary">
                            <option value="" disabled>-- Sélectionnez --</option>
                            <option value="male">Homme</option>
                            <option value="female">Femme</option>
                            <option value="other">Autre</option>
                        </select>
                    </div>

                    <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                            <label class="block text-sm text-secondary mb-1">Ville</label>
                            <Multiselect v-model="form.city_id" :options="cities" :multiple="false" track-by="id"
                                label="name" :searchable="true" :internal-search="false" :filter-results="false"
                                :value-consistency="true" value-prop="id" @search-change="onSearchCities"
                                placeholder="Saisir..." :loading="loadingCities" />
                        </div>

                        <div>
                            <label class="block text-sm text-secondary mb-1">Département</label>
                            <Multiselect v-model="form.department_id" :options="departments" :multiple="false"
                                track-by="id" label="name" :searchable="true" :internal-search="false"
                                :filter-results="false" :value-consistency="true" value-prop="id"
                                @search-change="onSearchDepartments" placeholder="Saisir..."
                                :loading="loadingDepartments" />
                        </div>

                        <div>
                            <label class="block text-sm text-secondary mb-1">Région</label>
                            <Multiselect v-model="form.region_id" :options="regions" :multiple="false" track-by="id"
                                label="name" :searchable="true" :internal-search="false" :filter-results="false"
                                :value-consistency="true" value-prop="id" @search-change="onSearchRegions"
                                placeholder="Saisir..." :loading="loadingRegions" />
                        </div>
                    </div>

                    <div class="flex justify-end pt-3">
                        <div>
                            <button type="submit" class="btn-gold" :disabled="loadingProfile">
                                <span v-if="!loadingProfile">Enregistrer les modifications</span>
                                <span v-else class="flex items-center gap-2">
                                    <span class="loader-sm"></span> Sauvegarde...
                                </span>
                            </button>
                            <p v-if="message"
                                :class="['text-center font-semibold', messageType === 'success' ? 'text-green-600' : 'text-red-600']">
                                {{ message }}
                            </p>
                        </div>
                    </div>
                </form>
            </section>

            <section class="pt-4 border-t border-gold/30">
                <h3 class="text-lg font-semibold text-gold mb-2">Changer mon mot de passe</h3>

                <form @submit.prevent="updatePassword" class="space-y-4">
                    <div>
                        <label class="block mb-1 text-sm text-secondary">Ancien mot de passe</label>
                        <input v-model="oldPassword" type="password" class="input-primary"
                            placeholder="Ancien mot de passe" required />
                    </div>

                    <div>
                        <label class="block mb-1 text-sm text-secondary">Nouveau mot de passe</label>
                        <input v-model="newPassword" type="password" class="input-primary"
                            placeholder="Nouveau mot de passe" required />
                    </div>

                    <div>
                        <label class="block mb-1 text-sm text-secondary">Confirmer le nouveau mot de passe</label>
                        <input v-model="confirmPassword" type="password" class="input-primary"
                            placeholder="Confirmez le mot de passe" required />
                    </div>

                    <div class="flex justify-between items-center pt-2">
                        <button type="submit" class="btn-gold" :disabled="loadingPassword">
                            <span v-if="!loadingPassword">Mettre à jour</span>
                            <span v-else class="flex items-center gap-2">
                                <span class="loader-sm"></span> Mise à jour...
                            </span>
                        </button>

                        <button @click="logout" type="button" class="btn-fun" :disabled="loadingPassword">
                            Se déconnecter
                        </button>
                    </div>
                </form>
            </section>

            <p v-if="messagePwd"
                :class="['text-center font-semibold', messageType === 'success' ? 'text-green-600' : 'text-red-600']">
                {{ messagePwd }}
            </p>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useAuthStore } from '@/stores/authStore'
import { useRouter } from 'vue-router'
import UserService from '@/services/user.service'
import AuthService from '@/services/auth.service'
import locationService from '@/services/location.service'
import Multiselect from '@vueform/multiselect'
import '@vueform/multiselect/themes/default.css'

const router = useRouter()
const userStore = useAuthStore()
const user = computed(() => userStore.user)

// === Données du formulaire profil ===
const form = ref({
    username: user.value?.username || '',
    email: user.value?.email || '',
    gender: user.value?.gender || '',
    birthdate: user.value?.birthdate || '',
    bio: user.value?.bio || '',
    city_id: user.value?.city_id || '',
    department_id: user.value?.department_id || '',
    // La valeur du Multiselect stockera l'ID (region_id)
    region_id: user.value?.region?.id || null,
})

const amount = computed(() => user.value?.amount || 0)

onMounted(async () => {
    await getProfile()
})

watch(() => form.value.department_id, async (newDeptId) => {
    if (!newDeptId) {
        // Si on efface le département, on n’efface pas la région automatiquement :
        // on laisse le user décider s’il veut la retirer.
        return
    }

    try {
        // Appelle ton API pour récupérer la région associée
        const department = await locationService.getDepartmentById(newDeptId)

        if (department?.region_id) {
            // Mets automatiquement à jour le champ région
            form.value.region_id = department.region_id

            // Facultatif : si tu veux afficher la région dans la liste du Multiselect :
            const regionObj = {
                id: department.Region.id,
                name: department.Region.name,
                code: department.Region.code
            }
            if (!regions.value.some(r => r.id === regionObj.id)) {
                regions.value.push(regionObj)
            }
        }
    } catch (err) {
        console.error("Erreur lors de la récupération de la région du département :", err)
    }
})

watch(() => form.value.city_id, async (newCityId) => {
    if (!newCityId) return

    try {
        const city = await locationService.getCityById(newCityId)

        // Toujours récupérer le département via l'API pour être sûr
        const department = await locationService.getDepartmentById(city.department.id)
        form.value.department_id = department.id

        if (!departments.value.some(d => d.id === department.id)) {
            departments.value.push({
                id: department.id,
                name: department.name,
                code: department.code
            })
        }

        const region = department.Region
        if (region) {
            form.value.region_id = region.id

            if (!regions.value.some(r => r.id === region.id)) {
                regions.value.push({
                    id: region.id,
                    name: region.name,
                    code: region.code
                })
            }
        }

    } catch (err) {
        console.error("Erreur lors de la récupération des données de la ville :", err)
    }
})




// Le tableau `regions` stockera les objets région pour l'affichage dans le Multiselect
const cities = ref([])
const regions = ref([])
const departments = ref([])
const loadingCities = ref(false)
const loadingRegions = ref(false)
const loadingDepartments = ref(false)

let debounceTimeout = null

/**
 * Gère la recherche asynchrone pour le Multiselect.
 * @param {string} query La chaîne de recherche saisie par l'utilisateur.
 */
const onSearchRegions = (query) => {
    clearTimeout(debounceTimeout)

    // Si la requête est trop courte ou vide
    if (!query || query.length < 2) {
        // Garde uniquement la région actuellement sélectionnée (si elle existe) dans la liste d'options
        // Cela permet de ne pas surcharger la liste lorsqu'il n'y a pas de recherche active.
        const currentRegionId = form.value.region_id
        if (currentRegionId) {
            const currentRegionObject = regions.value.find(r => r.id === currentRegionId)
            regions.value = currentRegionObject ? [currentRegionObject] : []
        } else {
            regions.value = [] // Liste vide si aucune région n'est sélectionnée.
        }
        return
    }

    debounceTimeout = setTimeout(async () => {
        loadingRegions.value = true
        try {
            const res = await locationService.getRegions(query)
            console.log("Régions reçues du service :", res)

            // Mappe les résultats en objets pour le Multiselect
            const newRegions = res.map(region => ({
                id: region.id,
                code: region.code,
                name: region.name
            }))

            // Remplace les options par les résultats de la recherche.
            // On ne doit pas permettre d'ajouter une région qui n'est pas dans les résultats de la recherche.
            regions.value = newRegions

        } catch (err) {
            console.error("Erreur lors de la récupération des régions :", err)
            regions.value = [] // Vide en cas d'erreur
        } finally {
            loadingRegions.value = false
        }
    }, 300) // Délai de 300ms pour le debounce
}

const onSearchDepartments = (query) => {
    clearTimeout(debounceTimeout)

    // Si la requête est trop courte ou vide
    if (!query || query.length < 2) {
        // Garde uniquement la région actuellement sélectionnée (si elle existe) dans la liste d'options
        // Cela permet de ne pas surcharger la liste lorsqu'il n'y a pas de recherche active.
        const currentDepartmentId = form.value.department_id
        if (currentDepartmentId) {
            const currentDepartmentObject = departments.value.find(r => r.id === currentDepartmentId)
            departments.value = currentDepartmentObject ? [currentDepartmentObject] : []
        } else {
            departments.value = [] // Liste vide si aucune région n'est sélectionnée.
        }
        return
    }

    debounceTimeout = setTimeout(async () => {
        loadingDepartments.value = true
        try {
            const res = await locationService.getDepartments(query)
            console.log("Départements reçues du service :", res)

            // Mappe les résultats en objets pour le Multiselect
            const newDepartments = res.map(department => ({
                id: department.id,
                code: department.code,
                name: department.name
            }))

            // Remplace les options par les résultats de la recherche.
            // On ne doit pas permettre d'ajouter une région qui n'est pas dans les résultats de la recherche.
            departments.value = newDepartments

        } catch (err) {
            console.error("Erreur lors de la récupération des départements :", err)
            departments.value = [] // Vide en cas d'erreur
        } finally {
            loadingDepartments.value = false
        }
    }, 300) // Délai de 300ms pour le debounce
}

const onSearchCities = (query) => {
    clearTimeout(debounceTimeout)

    if (!query || query.length < 2) {
        const currentCityId = form.value.city_id
        if (currentCityId) {
            const currentCity = cities.value.find(c => c.id === currentCityId)
            cities.value = currentCity ? [currentCity] : []
        } else {
            cities.value = []
        }
        return
    }

    debounceTimeout = setTimeout(async () => {
        loadingCities.value = true
        try {
            const res = await locationService.getCities(query)

            cities.value = res.map(city => ({
                id: city.id,
                name: city.name,
                code: city.code
            }))
        } catch (err) {
            console.error("Erreur lors de la récupération des villes :", err)
            cities.value = []
        } finally {
            loadingCities.value = false
        }
    }, 300)
}


const loadingProfile = ref(false)
const loadingPassword = ref(false)
const oldPassword = ref('')
const newPassword = ref('')
const confirmPassword = ref('')
const message = ref('')
const messagePwd = ref('')
const messageType = ref('success')

const getProfile = async () => {
    try {
        const res = await UserService.getProfile()
        const data = res

        form.value = {
            username: data.username || '',
            email: data.email || '',
            gender: data.gender || '',
            birthdate: data.birthdate || '',
            bio: data.bio || '',
            amount: data.amount || 0,
            city_id: data.city_id || '',
            department_id: data.department_id || '',
            // form.region_id contiendra l'ID
            region_id: data.region?.id || null,
        }

        // Région (pour initialiser les options du Multiselect avec la région de l'utilisateur)
        if (data.region) {
            const regionObject = { id: data.region.id, code: data.region.code, name: data.region.name }
            // Ajoute la région sélectionnée au tableau d'options pour qu'elle puisse s'afficher
            regions.value = [regionObject]
        }

        // Département (pour initialiser les options du Multiselect avec le département de l'utilisateur)
        if (data.department) {
            const departmentObject = { id: data.department.id, code: data.department.code, name: data.department.name }
            // Ajoute le département sélectionné au tableau d'options pour qu'il puisse s'afficher
            departments.value = [departmentObject]
        }

        // Ville (pour initialiser les options du Multiselect avec la ville de l'utilisateur)
        if (data.city) {
            const cityObject = {
                id: data.city.id,
                name: data.city.name,
                code: data.city.code
            }
            cities.value = [cityObject]
        }


    } catch (err) {
        console.error("Erreur lors de la récupération du profil :", err)
        message.value = "Erreur lors de la récupération du profil."
        messageType.value = 'error'
    }
}


// === Met à jour le profil ===
const updateProfile = async () => {
    loadingProfile.value = true
    message.value = ''

    try {
        const payload = {
            ...form.value,
            // Envoie l'ID directement
            region_id: form.value.region_id || null,
            department_id: form.value.department_id || null,
            city_id: form.value.city_id || null,
        }

        // Supprime l'ancienne clé 'region' si elle existe encore
        delete payload.region

        const res = await UserService.updateProfile(payload)
        console.log("Profil mis à jour :", res)
        message.value = 'Profil mis à jour avec succès ✅'
        messageType.value = 'success'
        setTimeout(() => {
            message.value = ''
        }, 3000)
    } catch (err) {
        message.value = err.response?.data?.message || "Erreur lors de la mise à jour du profil ❌"
        messageType.value = 'error'
        setTimeout(() => {
            message.value = ''
        }, 5000)
    } finally {
        loadingProfile.value = false
        getProfile() // Rafraîchit les données du profil après mise à jour
    }
}

// === Met à jour le mot de passe ===
const updatePassword = async () => {
    if (newPassword.value !== confirmPassword.value) {
        messagePwd.value = "Les nouveaux mots de passe ne correspondent pas."
        messageType.value = 'error'
        return
    }

    loadingPassword.value = true
    messagePwd.value = ''

    try {
        await AuthService.changePassword({
            currentPassword: oldPassword.value,
            newPassword: newPassword.value,
        })
        messagePwd.value = "Votre mot de passe a été mis à jour avec succès."
        messageType.value = 'success'
        oldPassword.value = ''
        newPassword.value = ''
        confirmPassword.value = ''
        setTimeout(() => {
            messagePwd.value = ''
        }, 3000)
    } catch (err) {
        messagePwd.value = err.response?.data?.message || "Erreur lors de la mise à jour du mot de passe."
        messageType.value = 'error'
        setTimeout(() => {
            messagePwd.value = ''
        }, 5000)
    } finally {
        loadingPassword.value = false
    }
}

// === Déconnexion ===
const logout = async () => {
    await userStore.logout()
    router.push({ name: 'LoginRegister', query: { view: 'login' } })
}
</script>

<style scoped>
.input-primary {
    @apply w-full p-2 rounded border border-gold/40 focus:ring-2 focus:ring-gold bg-backgroundLight;
}
</style>